import { useState, useEffect } from "react";
import "./ResponsePage.css";
import { useLocation } from "react-router-dom";
import { TransactionPayinRequestModel } from "../../model/TransactionPayinRequestModel";
import { msgTypes } from "../../constants/msgTypes.js";
import { Box, Typography, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const ResponsePage = () => {
  const location = useLocation();

  const [response, setResponse] = useState<TransactionPayinRequestModel & { message?: string }>(new TransactionPayinRequestModel());
  const [countdown, setCountdown] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [closeCountdown, setCloseCountdown] = useState(180);

  useEffect(() => {
    if (!isLoaded || response.returnUrl || response.status !== msgTypes.SUCCESS) {
      return;
    }

    const deadline = Date.now() + 180000;
    setCloseCountdown(180);
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setCloseCountdown(remaining);
      if (remaining === 0) {
        clearInterval(timer);
        window.close();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isLoaded, response.returnUrl, response.status]);

  useEffect(() => {
    if (location?.state !== undefined && location?.state?.response) {
      const responseData = location.state.response;
      console.log("Response from state:", responseData);
      setResponse(responseData);
      setIsLoaded(true);
    }
    if (location?.search !== undefined && location?.search !== "") {
      const queryString = location?.search.split("?")[1];
      const params = new URLSearchParams(queryString);
      const result = new TransactionPayinRequestModel();
      params.forEach((value, key) => {
        result[key] = value === "null" ? null : decodeURIComponent(value);
      });
      console.log("Response from URL params:", result);
      setResponse(result);
      setIsLoaded(true);
    }
  }, [location?.search, location?.state]);

  // Auto-redirect countdown
  useEffect(() => {
    if (
      response.returnUrl &&
      isLoaded &&
      (response.status === msgTypes.SUCCESS ||
        response.status === msgTypes.FAILED)
    ) {
      console.log("Starting countdown for redirect to:", response.returnUrl);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsRedirecting(true);
            console.log("Redirecting to:", response.returnUrl);
            setTimeout(() => {
              window.location.href = response.returnUrl;
            }, 500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [response.returnUrl, response.status, isLoaded]);

  const handleManualRedirect = () => {
    if (response.returnUrl) {
      window.location.href = response.returnUrl;
    }
  };

  const getStatusIcon = () => {
    if (response.status === msgTypes.SUCCESS) {
      return (
        <CheckCircleIcon
          sx={{
            fontSize: "80px",
            color: "#10b981",
            mb: 2,
          }}
        />
      );
    } else if (response.status === msgTypes.PENDING) {
      return (
        <PendingIcon
          sx={{
            fontSize: "80px",
            color: "#f59e0b",
            mb: 2,
          }}
        />
      );
    } else {
      return (
        <CancelIcon
          sx={{
            fontSize: "80px",
            color: "#ef4444",
            mb: 2,
          }}
        />
      );
    }
  };

  const getStatusTitle = () => {
    if (response.status === msgTypes.SUCCESS) {
      return "Payment Successful!";
    } else if (response.status === msgTypes.PENDING) {
      return "Payment Pending";
    } else {
      return "Payment Failed";
    }
  };

  const getStatusMessage = () => {
    if (response.status === msgTypes.SUCCESS) {
      return "Your payment has been processed successfully.";
    } else if (response.status === msgTypes.PENDING) {
      return "Your payment is being processed. Please wait.";
    } else {
      return "We couldn't process your payment. Please try again.";
    }
  };

  const getStatusColor = () => {
    if (response.status === msgTypes.SUCCESS) {
      return {
        bg: "rgba(16, 185, 129, 0.1)",
        border: "#10b981",
        gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      };
    } else if (response.status === msgTypes.PENDING) {
      return {
        bg: "rgba(245, 158, 11, 0.1)",
        border: "#f59e0b",
        gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      };
    } else {
      return {
        bg: "rgba(239, 68, 68, 0.1)",
        border: "#ef4444",
        gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      };
    }
  };

  const statusColors = getStatusColor();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Mobile-like phone container */}
      <Box
        sx={{
          maxWidth: "420px",
          width: "100%",
          background: "#ffffff",
          borderRadius: "24px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: statusColors.gradient,
            padding: "32px 24px",
            textAlign: "center",
            borderRadius: "0 0 40px 40px",
            position: "relative",
          }}
        >
          {getStatusIcon()}
          <Typography
            variant="h4"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              mb: 1,
              letterSpacing: "0.5px",
            }}
          >
            {getStatusTitle()}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              fontSize: "15px",
              fontWeight: 500,
            }}
          >
            {getStatusMessage()}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, pt: 4 }}>
          {/* Transaction Details */}
          {response.orderId && (
            <Box
              sx={{
                background: "#f9fafb",
                borderRadius: "16px",
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: 600,
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Transaction Details
              </Typography>

              {response.orderId && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Order ID
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#1f2937",
                      fontWeight: 600,
                      wordBreak: "break-all",
                    }}
                  >
                    {response.orderId}
                  </Typography>
                </Box>
              )}

              {response.txnId && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Transaction ID
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#1f2937",
                      fontWeight: 600,
                      wordBreak: "break-all",
                    }}
                  >
                    {response.txnId}
                  </Typography>
                </Box>
              )}

              {(response.custFirstName || response.custLastName) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Customer Name
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#1f2937",
                      fontWeight: 600,
                    }}
                  >
                    {[response.custFirstName, response.custLastName]
                      .filter(Boolean)
                      .join(" ")}
                  </Typography>
                </Box>
              )}

              {response.businessName && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Paid To
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#1f2937",
                      fontWeight: 600,
                    }}
                  >
                    {response.businessName}
                  </Typography>
                </Box>
              )}

              {response.amount && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Amount
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "20px",
                      color: "#1f2937",
                      fontWeight: 700,
                    }}
                  >
                    ₹{parseFloat(response.amount).toFixed(2)}
                  </Typography>
                </Box>
              )}

              {response.message && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#9ca3af",
                      mb: 0.5,
                    }}
                  >
                    Message
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#1f2937",
                      fontWeight: 500,
                    }}
                  >
                    {response.message}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Redirect countdown */}
          {response.returnUrl &&
            (response.status === msgTypes.SUCCESS ||
              response.status === msgTypes.FAILED) && (
              <Box
                sx={{
                  background: statusColors.bg,
                  border: `2px solid ${statusColors.border}`,
                  borderRadius: "16px",
                  p: 3,
                  mb: 3,
                  textAlign: "center",
                }}
              >
                {isRedirecting ? (
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#1f2937",
                      fontWeight: 600,
                    }}
                  >
                    Redirecting...
                  </Typography>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#6b7280",
                        mb: 1,
                      }}
                    >
                      Redirecting in
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "32px",
                        color: statusColors.border,
                        fontWeight: 700,
                      }}
                    >
                      {countdown}s
                    </Typography>
                  </>
                )}
              </Box>
            )}

          {/* Action Button */}
          {response.returnUrl && (
            <Button
              onClick={handleManualRedirect}
              fullWidth
              sx={{
                background: statusColors.gradient,
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: 700,
                py: 2,
                borderRadius: "16px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${statusColors.border}40`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 12px 28px ${statusColors.border}60`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Return to Merchant
            </Button>
          )}

          {/* No redirect URL message */}
          {!response.returnUrl && (
            <Box
              sx={{
                background: "#f9fafb",
                borderRadius: "16px",
                p: 3,
                textAlign: "center",
              }}
            >
              <ErrorOutlineIcon
                sx={{ fontSize: "40px", color: "#9ca3af", mb: 1 }}
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >
                You can close this window now.
              </Typography>
              {response.status === msgTypes.SUCCESS && (
                <Typography sx={{ fontSize: "14px", color: "#6b7280", mt: 1 }}>
                  {closeCountdown > 0
                    ? `Page will close in ${Math.floor(closeCountdown / 60)}:${String(closeCountdown % 60).padStart(2, "0")}.`
                    : "Please close this tab manually if it is still open."}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResponsePage;
